// src/utils/mode.ts
//
// Helpers for HAMRS's internal "mode" representation.
//
// A QSO stores its mode as a single string. ADIF parent/submode pairs are
// folded into a composite "PARENT---SUBMODE" form on import (see AdifImporter),
// so MODE=MFSK + SUBMODE=FT4 becomes "MFSK---FT4". Bare modes ("SSB", "FT8", or
// a deprecated bare "FT4") have no separator.
//
// Two concerns are served here:
//  1. Duplicate detection needs MODE=FT4 and MODE=MFSK+SUBMODE=FT4 to compare
//     equal -- canonicalModeKey / modesAreEquivalent.
//  2. The log entry editor's mode <select> must be able to display and preserve
//     whatever mode a QSO already has, including composites and other ADIF
//     modes that aren't built-in options -- mergeModeOptions / prettyModeLabel.

import { MODE_OPTIONS, MODE_SUBMODE_SEPARATOR, type Option } from '@/constants';
import { ADIF_SUBMODE_TO_MODE } from '@/constants/adifSubmodes';

/**
 * Split a stored mode into its non-empty, trimmed, upper-cased segments.
 * "MFSK---FT4" -> ["MFSK", "FT4"]; "SSB" -> ["SSB"]; "" / null -> [].
 */
function modeSegments(value: string | null | undefined): string[] {
  if (!value) return [];
  return value
    .toUpperCase()
    .split(MODE_SUBMODE_SEPARATOR)
    .map((segment) => segment.trim())
    .filter(Boolean);
}

/**
 * Reduce a stored mode to a single canonical token for equality comparisons.
 * Mirrors `normalizeSpotMode` in bandPlanPrivileges: the most specific token
 * (the submode) wins, so MODE=FT4 ("FT4") and MODE=MFSK+SUBMODE=FT4
 * ("MFSK---FT4") both reduce to "FT4". Returns "" for empty/blank modes.
 */
export function canonicalModeKey(value: string | null | undefined): string {
  const segments = modeSegments(value);
  return segments[segments.length - 1] ?? '';
}

/**
 * True when two stored modes refer to the same effective mode. Treats a parent
 * mode plus submode as equivalent to the bare submode (FT4 and MFSK---FT4
 * compare equal) and treats empty/undefined modes as equal to each other.
 */
export function modesAreEquivalent(
  a: string | null | undefined,
  b: string | null | undefined
): boolean {
  return canonicalModeKey(a) === canonicalModeKey(b);
}

/**
 * Human-friendly label for a stored mode value. Composites render their parts
 * joined ("MFSK---FT4" -> "MFSK / FT4"); everything else is shown as-is.
 */
export function prettyModeLabel(value: string): string {
  const parts = value
    .split(MODE_SUBMODE_SEPARATOR)
    .map((part) => part.trim())
    .filter(Boolean);
  return parts.length > 1 ? parts.join(' / ') : value;
}

/**
 * Resolve a stored mode into the MODE/SUBMODE pair to write to an ADIF file.
 *
 * A composite ("MFSK---FT4") splits into its parts unchanged. A bare token
 * that is actually an ADIF submode (a bare "FT4" from WSJT-X or the mode
 * <select>, a "USB" from a legacy import) is paired with its parent from the
 * spec's Submode enumeration: FT4 -> MODE=MFSK + SUBMODE=FT4. Valid bare
 * modes (SSB, FT8, CW, ...) and unrecognized values pass through with no
 * submode.
 */
export function toAdifModeAndSubmode(value: string | null | undefined): {
  mode: string;
  submode: string | null;
} {
  if (!value) return { mode: '', submode: null };
  const [mode, submode] = value.split(MODE_SUBMODE_SEPARATOR);
  if (submode !== undefined) {
    return { mode, submode: submode.trim() || null };
  }
  const token = mode.trim().toUpperCase();
  const parent = ADIF_SUBMODE_TO_MODE[token];
  return parent ? { mode: parent, submode: token } : { mode, submode: null };
}

/**
 * The standard mode <select> options, guaranteeing that `currentValue` is
 * selectable. When a QSO carries a mode that isn't a built-in option -- an
 * imported parent/submode composite like "MFSK---FT4", or any other ADIF mode --
 * it's appended so the editor can display and preserve it instead of silently
 * coercing it to the default (SSB).
 */
export function mergeModeOptions(currentValue?: string | null): Option[] {
  const options = [...MODE_OPTIONS];
  const value = (currentValue ?? '').trim();
  if (!value) return options;
  // Compare the whole value case-insensitively (matching canonicalModeKey's
  // casing) so a stored "ssb" isn't appended as a near-duplicate of "SSB". This
  // is a full-string check, not canonicalModeKey, so a composite like
  // "MFSK---FT4" still does not collapse into the bare "FT4" option.
  const upper = value.toUpperCase();
  if (options.some((option) => option.value.toUpperCase() === upper)) return options;
  return [...options, { value, label: prettyModeLabel(value) }];
}
